import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import MaskedInput from "react-text-mask";

import InputField from "~components/Form/components/InputField";
import Select from "~components/Form/components/Select";

import mask from "~services/utils/mask";

import useAddressForm from "~components/Form/hooks/useAddressForm";
import useHeader from "~components/Header/hooks/useHeader";
import ModalCepOperacional from "~components/Modal/ModalCepOperacional";
import { validarCepExOperacional } from "~fetchs/useRuas";

const AddressForm = forwardRef(({ formikRef, disabled, required = true, dataComingBack, emissorData = {} }, ref) => {
  const { cepMask, formatCepValue } = mask();
  const {
    cep,
    cidade,
    estado,
    handleCepOnBlur,
    handleCepOnChange,
    cepModal,
    handleSelecionarRua,
    handleFecharModalSemSelecionar,
    verificarCep,
  } = useAddressForm(formikRef, dataComingBack, !disabled);

  useImperativeHandle(ref, () => ({ verificarCep }), [verificarCep]);
  const { path } = useHeader();
  const [isCepRequired, setIsCepRequired] = useState(null)
  const [isDisabledEmissor, setIsDisabledEmissor] = useState(false)
  const [blockedEndereco, setBlockedEndereco] = useState(false);
  const cepLookupTriggeredRef = useRef(false);

  const prevFormikAddrRef = useRef({ cidadeId: null, uf: null });
  const prevOptionsLenRef = useRef({ estadoLen: null, cidadeLen: null });

  useEffect(() => {
    const snapshot = formikRef?.current?.values || {};
    if (
      prevFormikAddrRef.current.cidadeId !== snapshot.cidadeId ||
      prevFormikAddrRef.current.uf !== snapshot.uf
    ) {
      prevFormikAddrRef.current = { cidadeId: snapshot.cidadeId, uf: snapshot.uf };
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRef?.current?.values?.cidadeId, formikRef?.current?.values?.uf]);

  useEffect(() => {
    const estadoLen = (estado?.options || []).length;
    const cidadeLen = (cidade?.options || []).length;
    if (
      prevOptionsLenRef.current.estadoLen !== estadoLen ||
      prevOptionsLenRef.current.cidadeLen !== cidadeLen
    ) {
      prevOptionsLenRef.current = { estadoLen, cidadeLen };
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado?.options?.length, cidade?.options?.length]);

  useEffect(() => {
    const cepEmissor = emissorData?.cep;
    const numeroEmissor = emissorData?.numero;
    const ruaEmissor = emissorData?.rua;

    const cepRecebedor = formikRef?.current?.values?.cep;
    const numeroRecebedor = formikRef?.current?.values?.numero;
    const ruaRecebedor = formikRef?.current?.values?.rua;

    const cepIgual = cepEmissor === cepRecebedor;
    const numeroIgual = numeroEmissor === numeroRecebedor;
    const ruaIgual = ruaEmissor === ruaRecebedor;

    if ((cepIgual && numeroIgual && ruaIgual) || (cepIgual && numeroIgual)) {
      setBlockedEndereco(true);
    }
    else {
      setBlockedEndereco(false);
    }
  }, [emissorData, formikRef?.current]);
  
  useEffect(() => {
    if (location.pathname === '/carteira/prospect/novo' || /\/carteira\/prospect\/\d+/.test(location.pathname)) {
      setIsCepRequired(true)
    }
    if ((/^\/carteira\/recebedor\/\d+$/.test(location.pathname) || /^\/carteira\/emissor\/\d+$/.test(location.pathname)) && formikRef?.current?.values?.tipoN == 2) {
      setIsDisabledEmissor(true);
    }
  }, [path, formikRef?.current]);

  useEffect(() => {
      formikRef?.current?.setFieldValue('enderecoBloqueado', blockedEndereco);
    }, [blockedEndereco]);
    
  useEffect(() => {
    const formik = formikRef?.current;
    if (!formik) return;

    const rawCep = formik.values?.cep || "";
    if (!rawCep) return;

    const formatted = formatCepValue(rawCep);
    if (formatted && formatted !== rawCep && !formik.touched?.cep) {
      formik.setFieldValue("cep", formatted, false);
    }
  }, [formikRef?.current?.values?.cep, formikRef?.current?.touched?.cep]);

  useEffect(() => {
    const formik = formikRef?.current;
    if (!formik) return;

    const digits = String(formik.values?.cep || "").replace(/\D/g, "");
    const hasCidadeUf = Boolean(formik.values?.cidadeId) && Boolean(formik.values?.uf);

    if (digits.length === 8 && !formik.touched?.cep && !hasCidadeUf && !cepLookupTriggeredRef.current) {
      cepLookupTriggeredRef.current = true;
      handleCepOnBlur(digits);
    }
  }, [
    formikRef?.current?.values?.cep,
    formikRef?.current?.touched?.cep,
    formikRef?.current?.values?.cidadeId,
    formikRef?.current?.values?.uf
  ]);

  return (
    <>
      <div className="grid gap-x-4 md:grid-cols-1 lg:grid-cols-4">
        <InputField
          labelName="CEP:"
          inputName="cep"
          as={MaskedInput}
          mask={cepMask}
          inputMask="onmask"
          inputType="text"
          validate={disabled ? undefined : validarCepExOperacional}
          onBlur={(el) => {
            handleCepOnBlur(el.target.value)
          }}
          onChangeEvent={(el) => {
            const active = document.activeElement == el.target;
            handleCepOnChange(el.target.value, active)
          }}
          isRequired={isCepRequired}
          loading={cep.loading}
          isDisabled={disabled}
        />

        <InputField
          labelName="Número:"
          inputName="numero"
          inputMask="offmask"
          inputType="text"
          isRequired={required}
          isDisabled={isDisabledEmissor}
        />

        <InputField
          labelName="Complemento:"
          inputName="complemento"
          inputMask="offmask"
          inputType="text"
          isDisabled={isDisabledEmissor}
        />

        <Select
          name="uf"
          label="Estado:"
          placeholder="Estado:"
          options={estado.options}
          noOptionsMessage="Estado não encontrado"
          errors={formikRef?.current?.errors?.uf}
          onChange={(opt) => {
            estado.handleChange(opt)
          }}
          touched={formikRef?.current?.touched?.uf}
          loading={estado.loading}
          {...estado.select}
          isRequired={required}
          isDisabled={disabled}
        />
      </div>

      <div className="grid gap-x-4 grid-cols-2 lg:grid-cols-3">
        <InputField
          labelName="Rua:"
          inputName="rua"
          inputMask="offmask"
          inputType="text"
          isRequired={required}
          isDisabled={disabled}
        />

        <InputField
          labelName="Bairro:"
          inputName="bairro"
          inputMask="offmask"
          inputType="text"
          isRequired={required}
          isDisabled={disabled}
        />

        <Select
          name="cidadeId"
          label="Cidade:"
          placeholder="Cidade:"
          options={cidade.options}
          noOptionsMessage="Cidade não encontrada"
          onChange={(e) => {
            cidade.handleChange(e)
          }}
          errors={formikRef?.current?.errors?.cidadeId}
          touched={formikRef?.current?.touched?.cidadeId}
          loading={cidade.loading}
          isRequired={required}
          {...cidade.select}
          isDisabled={disabled}
        />
        <InputField inputType={"hidden"} inputName="enderecoBloqueado" inputValue={blockedEndereco} />
      </div>

      <ModalCepOperacional
        isOpen={cepModal.isOpen}
        onClose={handleFecharModalSemSelecionar}
        ruas={cepModal.ruas}
        onSelect={handleSelecionarRua}
      />
    </>
  );
});

AddressForm.displayName = "AddressForm";

export default AddressForm;
